import { useState } from "react";
import useClientes from "../../hooks/useClientes";
import useAgendarAtendimento from "../../hooks/useAgendarAtendimento";
import { formatarNomeUpper, aplicarMascaraCPF, validarCPF } from "../../utils/formatadores";

const ModalAgendarAtendimento = ({ onClose }) => {
  const { buscarCliente } = useClientes();
  const {
    agendamentoExistente,
    loading: loadingAgendamento,
    buscarAgendamentoPorCliente,
    agendar,
    atualizar,
    limparAgendamentoExistente
  } = useAgendarAtendimento();

  const [busca, setBusca] = useState("");
  const [clienteEncontrado, setClienteEncontrado] = useState(null);
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [buscaRealizada, setBuscaRealizada] = useState(false);

  const [dataAgendamento, setDataAgendamento] = useState("");
  const [horaAgendamento, setHoraAgendamento] = useState("");
  const [modoEdicao, setModoEdicao] = useState(false);
  const [actionType, setActionType] = useState(null);

  // 🔍 BUSCAR CLIENTE
  const handleBuscar = async () => {
    if (!busca.trim()) return;

    const somenteNumeros = busca.replace(/\D/g, "");
    if (somenteNumeros.length === 11 && !validarCPF(busca)) {
      alert("CPF inválido");
      return;
    }

    setActionType("buscar");
    try {
      const resultados = await buscarCliente(busca);
      setResultadosBusca(resultados);
      setClienteEncontrado(null);
      setDataAgendamento("");
      setHoraAgendamento("");
      limparAgendamentoExistente();
      setModoEdicao(false);
      setBuscaRealizada(true);
    } catch (err) {
      console.error(err);
      alert("Erro ao buscar cliente");
    } finally {
      setActionType(null);
    }
  };

  // 👤 SELECIONAR CLIENTE (Busca o agendamento mas MANTÉM os campos vazios)
  const handleSelecionarCliente = async (cliente) => {
    setClienteEncontrado(cliente);
    setResultadosBusca([]);
    setModoEdicao(false);
    setDataAgendamento("");
    setHoraAgendamento("");

    await buscarAgendamentoPorCliente(cliente._id);
  };

  // ✏️ ATIVAR MODO DE EDIÇÃO (Aqui sim preenche os campos com o registro existente)
  const handleAtivarEdicaoAgendamento = () => {
    if (agendamentoExistente) {
      setDataAgendamento(agendamentoExistente.dataAgendamento || "");
      setHoraAgendamento(agendamentoExistente.horaAgendamento || "");
      setModoEdicao(true);
    }
  };

  // 🧹 TROCAR CLIENTE
  const handleTrocarCliente = () => {
    setClienteEncontrado(null);
    setDataAgendamento("");
    setHoraAgendamento("");
    setModoEdicao(false);
    limparAgendamentoExistente();
  };

  // 💾 SALVAR / ATUALIZAR AGENDAMENTO
  const handleSalvar = async () => {
    if (!clienteEncontrado) {
      alert("Selecione um cliente antes de salvar.");
      return;
    }

    if (!dataAgendamento || !horaAgendamento) {
      alert("Informe a data e o horário.");
      return;
    }

    setActionType("salvar");
    try {
      if (modoEdicao && agendamentoExistente) {
        // Modo Edição: envia requisição PUT
        await atualizar(agendamentoExistente._id, {
          dataAgendamento,
          horaAgendamento
        });
        alert("Agendamento atualizado com sucesso!");
      } else {
        // Bloqueia novo agendamento se houver um em aberto e não estiver em modo edição
        if (agendamentoExistente) {
          alert(
            "O cliente já possui um agendamento em aberto. Clique em 'Editar este Agendamento' para alterar os dados."
          );
          setActionType(null);
          return;
        }

        // Modo Criação: envia requisição POST
        await agendar({
          clienteId: clienteEncontrado._id,
          dataAgendamento,
          horaAgendamento,
          dataHoraFormatada: `${dataAgendamento}T${horaAgendamento}:00`
        });
        alert("Atendimento agendado com sucesso!");
      }

      onClose();
    } catch (err) {
      console.error(err);
      alert(err.message || "Erro ao salvar agendamento.");
    } finally {
      setActionType(null);
    }
  };

  return (
    <div className="modal-overlay" style={overlayStyle}>
      <div className="card" style={modalContentStyle}>
        <h2>{modoEdicao ? "EDITAR AGENDAMENTO" : "AGENDAR ATENDIMENTO"}</h2>

        {!clienteEncontrado && (
          <>
            <form
              className="search-inline"
              onSubmit={(e) => {
                e.preventDefault();
                handleBuscar();
              }}
            >
              <input
                placeholder="CPF ou Nome"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
              <button
                type="submit"
                className="botao-primary"
                disabled={actionType !== null}
              >
                {actionType === "buscar" ? (
                  <span className="spinner-loader"></span>
                ) : (
                  "Buscar"
                )}
              </button>
            </form>

            {resultadosBusca.length > 0 && (
              <div className="card" style={{ marginTop: "15px" }}>
                <h3>Clientes encontrados:</h3>
                {resultadosBusca.map((c) => (
                  <div
                    className="action-buttons"
                    key={c._id}
                    style={{
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px"
                    }}
                  >
                    <span>
                      {formatarNomeUpper(c.nomeCliente)} - {aplicarMascaraCPF(c.cpf)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleSelecionarCliente(c)}
                    >
                      Selecionar
                    </button>
                  </div>
                ))}
              </div>
            )}

            {buscaRealizada && resultadosBusca.length === 0 && (
              <div
                className="card"
                style={{ marginTop: "15px", textAlign: "center" }}
              >
                <p>Nenhum cliente encontrado para "{busca}".</p>
              </div>
            )}
          </>
        )}

        {clienteEncontrado && (
          <div className="card" style={{ marginTop: "15px" }}>
            <p>
              <b>Cliente:</b> {clienteEncontrado.nomeCliente}
            </p>
            <p>
              <b>CPF:</b> {clienteEncontrado.cpf}
            </p>
          </div>
        )}

        <hr style={{ margin: "20px 0" }} />

        {/* CAMPOS DE DATA E HORA */}
        <div className="form-grid">
          <div className="form-group">
            <label>
              Data <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="date"
              className="input"
              value={dataAgendamento}
              onChange={(e) => setDataAgendamento(e.target.value)}
              disabled={
                (!!agendamentoExistente && !modoEdicao) || loadingAgendamento
              }
            />
          </div>

          <div className="form-group">
            <label>
              Hora <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="time"
              className="input"
              value={horaAgendamento}
              onChange={(e) => setHoraAgendamento(e.target.value)}
              disabled={
                (!!agendamentoExistente && !modoEdicao) || loadingAgendamento
              }
            />
          </div>
        </div>

        {loadingAgendamento && (
          <p style={{ marginTop: "15px", textAlign: "center", color: "#666" }}>
            Verificando agendamentos em aberto...
          </p>
        )}

        {/* CARD DE AVISO COM BOTÃO PARA DISPARAR A EDIÇÃO */}
        {agendamentoExistente && !loadingAgendamento && (
          <div
            className="card"
            style={{
              marginTop: "20px",
              backgroundColor: modoEdicao ? "#e2f0d9" : "#fff3cd",
              padding: "12px 15px"
            }}
          >
            <h4
              style={{
                color: modoEdicao ? "#155724" : "#856404",
                marginTop: 0,
                marginBottom: "8px"
              }}
            >
              {modoEdicao
                ? "✏️ Modo de Edição Ativo"
                : "⚠️ Agendamento em Aberto Encontrado"}
            </h4>
            
            <p style={{ margin: "4px 0", color: modoEdicao ? "#155724" : "#856404" }}>
              <b>Status Atual:</b>{" "}
              <span style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                {agendamentoExistente.status}
              </span>
            </p>

            <p style={{ margin: "4px 0", color: modoEdicao ? "#155724" : "#856404" }}>
              <b>Data:</b>{" "}
              <span style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                {agendamentoExistente.dataAgendamento}
              </span>
            </p>

            <p style={{ margin: "4px 0", color: modoEdicao ? "#155724" : "#856404" }}>
              <b>Hora:</b>{" "}
              <span style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                {agendamentoExistente.horaAgendamento}
              </span>
            </p>

            {!modoEdicao && (
              <button
                type="button"
                className="botao-primary"
                style={{
                  marginTop: "10px",
                  fontSize: "12px",
                  padding: "6px 12px"
                }}
                onClick={handleAtivarEdicaoAgendamento}
              >
                Editar este Agendamento
              </button>
            )}
          </div>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="btn-save"
            onClick={handleSalvar}
            disabled={
              actionType !== null ||
              (!!agendamentoExistente && !modoEdicao) ||
              loadingAgendamento
            }
          >
            {actionType === "salvar" ? (
              <span className="spinner-loader"></span>
            ) : modoEdicao ? (
              "Atualizar Agendamento"
            ) : (
              "Salvar"
            )}
          </button>

          {clienteEncontrado && (
            <button
              type="button"
              onClick={handleTrocarCliente}
              disabled={actionType !== null || loadingAgendamento}
            >
              Trocar Cliente
            </button>
          )}

          <button
            className="btn-cancel"
            type="button"
            onClick={onClose}
            disabled={actionType !== null}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000
};

const modalContentStyle = {
  width: "100%",
  maxWidth: "550px",
  backgroundColor: "#fff",
  padding: "20px",
  borderRadius: "8px"
};

export default ModalAgendarAtendimento;